import {
  EntityRefUtil,
  FORM_FIELD_PREFIX,
  formatDateWithUserTimezone,
  FormElement,
  FormElementKind,
  getFormFieldColumnHeader,
  GetLocationsResponseDTO,
  GetRepairsRequestDTO,
  GetRepairsResponseDTO,
  GetUnitsResponseDTO,
  RecordState,
} from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import CameraIcon from '@/components/icons/camera';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { getFileTypeIcon } from '@/utils/file-type';

const columnHelper = createColumnHelper<GetRepairsResponseDTO['data'][number]>();

// Element kinds that should NOT be rendered as columns
const EXCLUDED_ELEMENT_KINDS: FormElementKind[] = [
  FormElementKind.SignatureArray,
  FormElementKind.Table,
  FormElementKind.PreFilledText,
  FormElementKind.AssetSelect,
  FormElementKind.Heading,
  FormElementKind.Space,
  FormElementKind.IssueSelect,
];

function shouldRenderColumnForElement(element: FormElement): boolean {
  if (EXCLUDED_ELEMENT_KINDS.includes(element.kind)) return false;
  return getFormFieldColumnHeader(element) !== null;
}

type FilterType = 'text' | 'date-range' | 'multi-select' | 'async-multi-select';

function getFilterTypeForElement(element: FormElement): FilterType | undefined {
  switch (element.kind) {
    case FormElementKind.Date:
      return 'date-range';
    case FormElementKind.Dropdown:
    case FormElementKind.PassFailCheck:
    case FormElementKind.PassFailWithNaCheck:
      return 'multi-select';
    case FormElementKind.LocationSelect:
      return 'async-multi-select';
    case FormElementKind.SingleLineInput:
    case FormElementKind.MultiLineInput:
    case FormElementKind.Number:
    case FormElementKind.UnitReading:
    case FormElementKind.ReadingHours:
    case FormElementKind.ReadingMileage:
      return 'text';
    case FormElementKind.Photo:
    default:
      return undefined;
  }
}

function getFilterOptionsForElement(element: FormElement): Array<{ label: string; value: string }> | undefined {
  switch (element.kind) {
    case FormElementKind.Dropdown:
      if (element.config?.source === 'static' && element.config?.options) {
        return element.config.options.map((opt: string) => ({ label: opt, value: opt }));
      }
      return undefined;
    case FormElementKind.PassFailCheck:
      return [
        { label: 'Pass', value: 'pass' },
        { label: 'Fail', value: 'fail' },
      ];
    case FormElementKind.PassFailWithNaCheck:
      return [
        { label: 'Pass', value: 'pass' },
        { label: 'Fail', value: 'fail' },
        { label: 'N/A', value: 'na' },
      ];
    default:
      return undefined;
  }
}

function renderCellByElementKind(
  row: GetRepairsResponseDTO['data'][number],
  element: FormElement,
  units?: GetUnitsResponseDTO['data'],
  locations?: GetLocationsResponseDTO['data'],
): React.ReactNode {
  const answer = row.data?.find((a) => a?.elementId === element.id);

  if (!answer || answer.value == null) {
    return <span className="text-secondary-text">-</span>;
  }

  switch (element.kind) {
    case FormElementKind.SingleLineInput:
    case FormElementKind.MultiLineInput:
      return <span className="max-w-[200px] truncate">{String(answer.value)}</span>;

    case FormElementKind.Number:
    case FormElementKind.ReadingHours:
    case FormElementKind.ReadingMileage:
      return <span>{Number(answer.value).toLocaleString()}</span>;

    case FormElementKind.UnitReading: {
      const unitId = (element.config as { unitId?: string })?.unitId;
      const unit = units?.find((u) => u.id === unitId);
      return (
        <span>
          {Number(answer.value).toLocaleString()}
          {unit?.abbreviation && <span className="text-secondary-text"> {unit.abbreviation}</span>}
        </span>
      );
    }

    case FormElementKind.Date:
      try {
        return <span>{format(new Date(answer.value as string), 'MMM dd, yyyy hh:mm a')}</span>;
      } catch {
        return <span>{String(answer.value)}</span>;
      }

    case FormElementKind.Dropdown: {
      const values = Array.isArray(answer.value) ? answer.value : [answer.value];
      return <span>{values.filter(Boolean).join(', ')}</span>;
    }

    case FormElementKind.PassFailCheck:
    case FormElementKind.PassFailWithNaCheck: {
      const passFailValue = answer.value as { option?: string | null; issues?: string[] } | null;
      const option = passFailValue?.option;
      if (option === 'pass') {
        return <span className="text-default-green">Pass</span>;
      } else if (option === 'na') {
        return <span className="text-default-blue">N/A</span>;
      } else {
        return <span className="text-default-red">Fail</span>;
      }
    }

    case FormElementKind.Photo: {
      const photoCount = Array.isArray(answer.value) ? answer.value.length : 0;
      return photoCount ? (
        <span className="bg-default-light-blue text-default-dark-blue flex w-fit gap-0.5 rounded-[5px] px-[5px] py-[2px]">
          <CameraIcon />
          {photoCount}
        </span>
      ) : (
        <span className="text-secondary-text">-</span>
      );
    }

    case FormElementKind.PdfAttachment: {
      const pdfFiles = Array.isArray(answer.value) ? (answer.value as { url: string; name: string }[]) : [];
      return pdfFiles.length ? (
        <div className="flex flex-col gap-2">
          {pdfFiles.map((file, i) => (
            <span key={i} className="flex items-center gap-2">
              {getFileTypeIcon('application/pdf')}
              <span className="truncate text-sm">{file.name}</span>
            </span>
          ))}
        </div>
      ) : (
        <span className="text-secondary-text">-</span>
      );
    }

    case FormElementKind.LocationSelect: {
      const locationId = answer.value as string;
      const location = locations?.find((l) => l.id === locationId);
      return location?.name ?? <span className="text-secondary-text">-</span>;
    }

    default:
      return <span className="text-secondary-text">-</span>;
  }
}

function createFormFieldColumn(element: FormElement, units?: GetUnitsResponseDTO['data'], locations?: GetLocationsResponseDTO['data']) {
  const filterType = getFilterTypeForElement(element);
  const filterOptions = getFilterOptionsForElement(element);

  return columnHelper.accessor(
    (row) => {
      const answer = row.data?.find((a) => a?.elementId === element.id);
      return answer?.value ?? null;
    },
    {
      id: `formField-${element.id}`,
      header: element.kind === FormElementKind.PdfAttachment ? 'PDF Attachment(s)' : (getFormFieldColumnHeader(element) ?? element.label),
      cell: ({ row }) => renderCellByElementKind(row.original, element, units, locations),
      enableSorting: true,
      enableGrouping: false,
      enableColumnFilter: !!filterType,
      meta: {
        filterType,
        ...(filterType && { disableNullFilter: true }),
        getFilterOptionsFn:
          element.kind === FormElementKind.LocationSelect
            ? async (inputValue?: string, value?: string | string[]) => {
                const { data } = await (
                  await ApiService.GetLocations({ limit: 100, filters: { search: inputValue || undefined, state: RecordState.Active } })
                ).json();

                const resultsMap = new Map(data.map((location) => [location.id, { label: location.name, value: location.id }]));

                if (value) {
                  const valueArray = Array.isArray(value) ? value : [value];
                  const missingIds = valueArray.filter((id) => !resultsMap.has(id));
                  if (missingIds.length) {
                    const { data: missingLocations } = await (
                      await ApiService.GetLocations({
                        limit: 100,
                        filters: { fields: { id: missingIds } },
                      })
                    ).json();

                    for (const location of missingLocations) {
                      resultsMap.set(location.id, { label: location.name, value: location.id });
                    }
                  }
                }

                return Array.from(resultsMap.values());
              }
            : filterOptions
              ? async () => filterOptions
              : undefined,
      },
    },
  );
}

export const columns = (formElements?: FormElement[], units?: GetUnitsResponseDTO['data'], locations?: GetLocationsResponseDTO['data']) => {
  const userTimezone = useUserStore.getState().user?.timezone;
  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2">
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label="Select all"
            />
          </label>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
          </label>
        </div>
      ),
      meta: {
        cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
      },
      enableSorting: false,
      enableHiding: false,
    }),
    columnHelper.accessor((row) => formatDateWithUserTimezone(new Date(row.repairDate), userTimezone), {
      id: 'createdDate',
      header: 'Created Date',
      enableSorting: true,
      enableGrouping: true,
      meta: {
        filterType: 'date-range',
      },
    }),
    columnHelper.accessor((row) => format(new Date(row.repairDate), 'hh:mm a'), {
      id: 'createdTime',
      header: 'Created Time',
      enableSorting: true,
      enableGrouping: false,
    }),
    columnHelper.accessor('asset', {
      header: 'Asset',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{EntityRefUtil.createAssetLabel(value)}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: true,
      enableColumnFilter: true,
      getGroupingValue: (row) => (row.asset ? EntityRefUtil.createAssetLabel(row.asset) : 'None'),
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetAssets({ limit: 100, ...filters })).json();

          const resultsMap = new Map(data.map((asset) => [asset.id, { label: EntityRefUtil.createAssetLabel(asset), value: asset.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingAssets } = await (
                await ApiService.GetAssets({
                  limit: 100,
                  filters: { fields: { id: missingIds } },
                })
              ).json();

              for (const asset of missingAssets) {
                resultsMap.set(asset.id, { label: EntityRefUtil.createAssetLabel(asset), value: asset.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor((row) => row.repairedByUser, {
      id: 'repairedBy',
      header: 'Created By',
      cell: ({ getValue }) => {
        const value = getValue();
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(value?.firstName || '');

        return value ? (
          <div className="flex items-center gap-2">
            <Avatar className="size-[28px]">
              <AvatarFallback className={cn('text-xs font-[700]', avatarBackgroundColorClass)}>
                {value.firstName?.charAt(0)}
                {value.lastName?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm">{EntityRefUtil.createUserFullNameRef(value)}</span>
          </div>
        ) : (
          <span className="text-secondary-text">-</span>
        );
      },
      getGroupingValue: (row) => {
        return EntityRefUtil.createUserFullNameRef(row.repairedByUser, 'None');
      },
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data: sitemateUserData } = await (
            await ApiService.GetUsers({ limit: 100, filters: { ...filters?.filters, userType: 'sitemate-users' } })
          ).json();
          const { data: userData } = await (await ApiService.GetUsers({ limit: 100, ...filters })).json();

          const resultsMap = new Map<string, { label: string; value: string }>();
          sitemateUserData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });
          userData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingSitemateUsers } = await (
                await ApiService.GetUsers({
                  limit: 100,
                  filters: { id: missingIds, userType: 'sitemate-users' },
                })
              ).json();
              const { data: missingUsers } = await (
                await ApiService.GetUsers({
                  limit: 100,
                  filters: { id: missingIds },
                })
              ).json();

              for (const user of missingSitemateUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
              for (const user of missingUsers) {
                if (user.firstName || user.lastName) {
                  resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
                }
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor('number', {
      header: 'Repair Number',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{`R${value}`}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: true,
    }),
    columnHelper.accessor('id', {
      header: 'Record ID',
      enableGrouping: false,
    }),
    columnHelper.accessor('asset.internalId', {
      id: 'assetInternalId',
      header: 'Asset ID',
      enableGrouping: false,
      enableColumnFilter: false,
    }),
    // Dynamic form field columns
    ...(formElements ? formElements.filter(shouldRenderColumnForElement).map((element) => createFormFieldColumn(element, units, locations)) : []),
  ] as AccessorKeyColumnDef<GetRepairsResponseDTO['data'][number]>[];
};

export const groupOptions = [
  { label: 'None', accessor: '' },
  { label: 'Asset', accessor: 'asset' },
  { label: 'Created By', accessor: 'repairedBy' },
] satisfies { label: string; accessor: string }[];

// Static filter field map for standard columns
export const FILTER_FIELD_MAP: Record<string, keyof NonNullable<NonNullable<GetRepairsRequestDTO['filters']>['fields']>> = {
  asset: 'assetId',
  createdDate: 'createdDate',
  repairedBy: 'repairedById',
};

export function isFormFieldColumn(columnId: string): boolean {
  return columnId.startsWith(FORM_FIELD_PREFIX);
}

export function getElementIdFromColumnId(columnId: string): string | null {
  if (!isFormFieldColumn(columnId)) return null;
  return columnId.replace(FORM_FIELD_PREFIX, '');
}
